if (!customElements.get('age-verifier')) {
  customElements.define(
    'age-verifier',
    class AgeVerifier extends ModalElement {
      constructor() {
        super();
    
        this.init();
      }
    
      get shouldLock() {
        return true;
      }
    
      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }
    
      get delay() {
        return 1;
      }
    
      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }
    
      get cookieName() {
        return 'concept:age-verifier';
      }
    
      get focusElement() {
        // Return the first button in the modal (the "Yes I am" button)
        return this.querySelector('.cookie__buttons button') || this.querySelector('button');
      }
    
      init() {
        if (this.testMode || !this.getCookie(this.cookieName)) {
          this.load(this.delay);
        }
      }
    
      load(delay) {
        if (Shopify.designMode) return;
    
        setTimeout(() => this.show(), delay * 1000);
      }
    
      beforeShow() {
        super.beforeShow();
        // Set proper ARIA state when showing modal
        this.setAttribute('aria-hidden', 'false');
        // Store currently focused element to restore later
        this.previouslyFocusedElement = document.activeElement;
      }
    
      afterShow() {
        // Don't call super.afterShow() to avoid the fragile base trap
        // Handle shouldLock manually instead
        if (this.shouldLock) {
          const ModalElement = this.constructor;
          if (!window.lockLayerCount) {
            window.lockLayerCount = new WeakMap();
          }
          if (!window.lockLayerCount.has(ModalElement)) {
            window.lockLayerCount.set(ModalElement, 0);
          }
          window.lockLayerCount.set(ModalElement, window.lockLayerCount.get(ModalElement) + 1);
          document.body.classList.remove('has-modal-opening');
          document.body.classList.add('has-modal-open');
        }
        
        this.classList.add('show-image');
        
        // Use only our stable manual focus trap
        setTimeout(() => {
          const firstButton = this.querySelector('.cookie__buttons button');
          if (firstButton) {
            firstButton.focus();
          }
          
          // Add manual tab trap for the two buttons
          this.setupManualFocusTrap();
        }, 100);
      }
    
      setupManualFocusTrap() {
        // Get only the two action buttons
        const buttons = Array.from(this.querySelectorAll('.cookie__buttons button, .cookie__buttons a'));
        
        if (buttons.length < 2) return;
        
        const firstButton = buttons[0];
        const lastButton = buttons[buttons.length - 1];
        
        // Store reference to remove listener later
        this.tabTrapHandler = (event) => {
          if (event.key !== 'Tab') return;
          
          // If on last button and tabbing forward, go to first
          if (document.activeElement === lastButton && !event.shiftKey) {
            event.preventDefault();
            firstButton.focus();
          }
          
          // If on first button and tabbing backward, go to last
          if (document.activeElement === firstButton && event.shiftKey) {
            event.preventDefault();
            lastButton.focus();
          }
        };
        
        this.addEventListener('keydown', this.tabTrapHandler);
      }
    
      cleanupManualFocusTrap() {
        if (this.tabTrapHandler) {
          this.removeEventListener('keydown', this.tabTrapHandler);
          this.tabTrapHandler = null;
        }
      }
    
      beforeHide() {
        super.beforeHide();
        // Set proper ARIA state when hiding modal
        this.setAttribute('aria-hidden', 'true');
        // Clean up manual focus trap
        this.cleanupManualFocusTrap();
      }
    
      afterHide() {
        // Handle shouldLock cleanup manually
        if (this.shouldLock) {
          const ModalElement = this.constructor;
          if (window.lockLayerCount && window.lockLayerCount.has(ModalElement)) {
            window.lockLayerCount.set(ModalElement, window.lockLayerCount.get(ModalElement) - 1);
            document.body.classList.toggle('has-modal-open', window.lockLayerCount.get(ModalElement) > 0);
          }
        }
        
        this.classList.remove('show-image');
    
        // Restore focus to previously focused element
        if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
          this.previouslyFocusedElement.focus();
        }
        this.previouslyFocusedElement = null;
    
        // Remove a cookie in case it was set in test mode
        if (this.testMode) {
          this.removeCookie(this.cookieName);
          return;
        }
    
        this.setCookie(this.cookieName, this.expiry);
      }
    
      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }
    
      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
      }
    
      removeCookie(name) {
        document.cookie = `${name}=; max-age=0`;
      }
    }
  );
}
